// todo : remove ignore statement when password reset modal works again
/* istanbul ignore file */
/* eslint-disable react/require-default-props */
import React, {
  useState, useEffect, useRef, useLayoutEffect, useContext, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { cn } from '@express-labs/raven-tools';
import LoginContainer from '../../../../containers/LoginContainer';
import { scrollStop } from '../../../../helpers';
import Search from '../Search';
import s from './StickyHeader.module.scss';
import routes from '../../../routes/routes';
import HoverableSlideOutModal from '../HoverableSlideOutModal/HoverableSlideOutModal';
import SearchIcon from '../Search/subcomponents/SearchIcon';
import { debounce, ResizeContext } from '../../../../contexts/Resize/Resize';

import { HeaderDynamicContext } from '../../../../contexts/HeaderDynamicData/HeaderDynamicData';
import BagModal from '../../DesktopHeader/subcomponents/BagModal';
import DesktopMyAccountModal from '../DesktopMyAccountModal';

let scrollFreezed;
const { body } = document;

function getPaddingRight() {
  const outer = document.createElement('div');
  const inner = document.createElement('div');
  outer.style.overflow = 'scroll';
  body.appendChild(outer);
  outer.appendChild(inner);
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
  body.removeChild(outer);
  return scrollbarWidth;
}

function StickyHeader(props) {
  const {
    hideTopHeader = false,
    topPosition = 0,
    onEnterHoverableModal = null,
    onLeaveHoverableModal = null,
  } = props;
  const headerContext = useContext(HeaderDynamicContext);
  const { width: windowWidth } = useContext(ResizeContext);
  const [hasScroll, setHasScroll] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const scrollBarWidth = getPaddingRight();
  /* istanbul ignore next */
  const [hoverableSlideOutModalTopPosition, setHoverableSlideOutModalTopPosition] = useState(
    topPosition > 0 ? `${topPosition}px` : '66px',
  );

  useEffect(() => () => {
    if (scrollFreezed) {
      clearTimeout(scrollFreezed)
    }}
  )

  useEffect(() => {
    /* istanbul ignore next */
    setHoverableSlideOutModalTopPosition(topPosition > 0 ? `${topPosition}px` : '62px');
  }, [topPosition]);

  const [hoverAccount, setHoverAccount] = useState(false);
  const [hoverBag, setHoverBag] = useState(false);
  const [hoverSearch, setHoverSearch] = useState(false);
  const headerRef = useRef();

  useEffect(() => {
    const hasScrollBar = body.scrollHeight > document.documentElement.clientHeight;
    setHasScroll(hasScrollBar);
  }, [windowWidth]);

  const handleFreeze = useCallback(() => {
    /* istanbul ignore next */
    if (scrollFreezed) {
      window.clearTimeout(scrollFreezed);
    }

    // Had to do this to account for local development or any other instances where we can't scroll
    /* istanbul ignore next */
    if (hasScroll) {
      body.style.paddingRight = `${scrollBarWidth}px`;
    }
    body.classList.add(s.scrollFreeze);
    /* istanbul ignore next */
    if (typeof onEnterHoverableModal === 'function') {
      onEnterHoverableModal();
    }
  }, [hasScroll, onEnterHoverableModal, scrollBarWidth]);

  /* istanbul ignore next */
  const handleMouseLeave = useCallback(() => {
    scrollFreezed = setTimeout(() => {
      window.clearTimeout(scrollFreezed);
      body.style.paddingRight = '0px';
      body.classList.remove(s.scrollFreeze);
      if (typeof hoverSlideLeaveModal === 'function') {
        onLeaveHoverableModal();
      }
    }, 400);
  }, [onLeaveHoverableModal]);

  const handleBagIconClick = useCallback(() => {
    window.location.assign(routes.bag.path);
  }, []);

  const myAccountClick = useCallback(() => {
    /* istanbul ignore next */
    if (headerContext?.customer?.loginStatus) {
      window.location.assign(routes.login.path);
    } else {
      setIsLoginModalOpen(true);
    }
  }, [headerContext?.customer?.loginStatus]);

  const handleBagIconTap = useCallback((e) => {
    e.preventDefault();
    if (hoverBag) {
      handleBagIconClick();
    }
    setHoverBag(!hoverBag);
    setHoverAccount(false);
    setHoverSearch(false);
    handleFreeze();
  }, [handleBagIconClick, handleFreeze, hoverBag]);

  const handleMyAccountTap = useCallback((e) => {
    e.preventDefault();
    if (hoverAccount) {
      myAccountClick();
    }
    setHoverAccount(!hoverAccount);
    setHoverBag(false);
    setHoverSearch(false);
    handleFreeze();
  }, [handleFreeze, hoverAccount, myAccountClick]);

  /* istanbul ignore next */
  const clearHover = useCallback(() => {
    setHoverSearch(false);
    setHoverAccount(false);
    setHoverBag(false);
    handleMouseLeave();
  },[handleMouseLeave]);

  const handleSearchTap = useCallback((e) => {
    e.preventDefault();
    setHoverSearch(!hoverSearch);
    setHoverAccount(false);
    setHoverBag(false);
    handleFreeze();
  },[handleFreeze, hoverSearch]);

  /* istanbul ignore next */
  const getBottomPosition = (current) => {
    const dimensions = current.getBoundingClientRect();
    return dimensions.top + dimensions.height;
  };


  const handleLoginModalClose = () => {
    handleMouseLeave();
  };

  const loginModalCloseCallback = () => {
    handleMouseLeave();
    setIsLoginModalOpen(false);
  };

  /* istanbul ignore next */
  useLayoutEffect(() => {
    const { current } = headerRef;

    /* istanbul ignore next */
    const handleBottomPositionChange = () => {
    /* Adding a check to ensure that current exists.  This should
    not happen since it is in the layout effect but taking extra caution.
    Ignoring coverage for the else block in coverage because it is unlikely
    and nothing happens if current is not truthy. */
      if (current) {
        const bottomPosition = getBottomPosition(current);
        setHoverableSlideOutModalTopPosition(`${bottomPosition}px`);
      }
    };

    const debounceOnMouseMove = () => {
      debounce(() => {
        handleBottomPositionChange();
      }, 50);
    };

    /* istanbul ignore next */
    scrollStop(() => handleBottomPositionChange(current));
    document.addEventListener('mousemove', debounceOnMouseMove);
    return () => {
      document.removeEventListener('mousemove', debounceOnMouseMove);
    };
  }, [headerRef]);

  const elementHovered = hoverAccount || hoverBag || hoverSearch;

  return (
    <div className={cn(hideTopHeader && s.iconContainer)}>
      <div
        className={cn(s.icons, 'ravenDesktopIcons', elementHovered ? 'hover' : '')}
        aria-label="icons"
        ref={headerRef}
      >
        <HoverableSlideOutModal
          modalClassName={cn(s.modal, s.searchModal)}
          containerClassName={cn(s.modalContainer, hoverSearch ? 'hover' : '')}
          onMouseEnter={handleFreeze}
          onMouseLeave={handleMouseLeave}
          target={
            <SearchIcon className={cn(s.iconBtn, s.searchBtn)} onTouchEnd={handleSearchTap} />
          }
          component={<Search />}
          topPosition={hoverableSlideOutModalTopPosition}
          overlayProps={{
            'aria-label': 'overlay',
            style: { top: hoverableSlideOutModalTopPosition },
            onTouchStart: clearHover,
          }}
        />
        <DesktopMyAccountModal
          modalClassName={s.modal}
          containerClassName={cn(s.modalContainer, hoverAccount ? 'hover' : '')}
          onMouseEnter={handleFreeze}
          onMouseLeave={handleMouseLeave}
          topPosition={hoverableSlideOutModalTopPosition}
          myAccountIconProps={{
            className: s.iconBtn,
            onMouseUp: myAccountClick,
            onTouchEnd: handleMyAccountTap,
          }}
          overlayProps={{
            'aria-label': 'overlay',
            style: { top: hoverableSlideOutModalTopPosition },
            onTouchStart: clearHover,
          }}
          onLoginClose={handleLoginModalClose}
          removeScrollFreeze={handleMouseLeave}
        />
        <BagModal
          modalClassName={s.modal}
          onMouseEnter={handleFreeze}
          onMouseLeave={handleMouseLeave}
          containerClassName={cn(s.modalContainer, hoverBag ? 'hover' : '')}
          topPosition={hoverableSlideOutModalTopPosition}
          bagIconProps={{
            onMouseUp: handleBagIconClick,
            onTouchEnd: handleBagIconTap,
          }}
          overlayProps={{
            'aria-label': 'overlay',
            style: { top: hoverableSlideOutModalTopPosition },
            onTouchStart: clearHover,
          }}
        />
      </div>
      {isLoginModalOpen && (
        <LoginContainer isModalOpen={isLoginModalOpen} onModalClose={loginModalCloseCallback} />
      )}
    </div>
  );
}

StickyHeader.propTypes = {
  hideTopHeader: PropTypes.bool,
  topPosition: PropTypes.number,
  onEnterHoverableModal: PropTypes.func,
  onLeaveHoverableModal: PropTypes.func,
};

export default StickyHeader;
